import {
  Container,
  FormGroup,
  Input,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Button,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useEffect, useState, useRef, ChangeEvent } from "react";
import { Toggle, Whisper, Popover } from "rsuite";
import dayjs from "dayjs";

import useBackupsHistory from "../../../api/api-hooks/cloud/backups/useBackupsHistory";
import useBackupCloud from "../../../api/api-hooks/cloud/backups/useBackupCloud";
import FilterDropdownMenu from "../../../components/CRM/FilterDropdownMenu";
import SortBy from "../../../components/PageView/PageViewTable/SortBy";
import AppPageHeader from "../../../components/Headers/AppPageHeader";
import AppHelmet from "../../../components/AppComponents/AppHelmet";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import AppEmptyState from "../../../elements/AppEmptyState";
import { newToastEvent } from "../../../utils/ToastUtil";
import ListItem from "../../../components/CRM/ListItem";
import TenantAPI from "../../../api/api/TenantAPI";
import useModule from "../../../hooks/useModule";
import BackupOptions from "./BackupOptions";
dayjs.extend(advancedFormat);

const pageTabs = [
  {
    title: "Manage Backups",
    activeValue: "main",
  },
  {
    title: "Automatic Backup Settings",
    activeValue: "settings",
  },
];

export default function CloudBackup() {
  const navigate = useNavigate();
  const { tenantID, tab } = useParams();
  const {
    backupHistory,
    minRecords,
    maxRecords,
    hasBack,
    hasNext,
    backupHistoryLoading,
    goNext,
    goBack,
    onChangeCount,
    recordCount,
    sortParam,
    setSortParam,
    filter,
    setFilter,
  } = useBackupsHistory();
  const { backupCloud, loadingBackup } = useBackupCloud();
  const [loadingAutomatic, setAutomatic] = useState(false);
  const [currentIntervalData, setCurrent] = useState<{
    interval: string;
    active: boolean;
  } | null>();
  const [intervalData, setInterval] = useState<{
    interval: string;
    active: boolean;
  }>({
    interval: "",
    active: false,
  });
  const [active, setActive] = useState({ filter: false, sort: false });

  const { module } = useModule("CloudBackups");

  const filterRef = useRef(null);

  const handleTabChange = (val: string) => {
    navigate(`/clouds/${tenantID}/settings/backup/${val}`);
  };

  const backupCloudNow = () => {
    backupCloud({
      onSuccess: () =>
        newToastEvent(
          "primary",
          "Successfully started cloud backup.",
          "backup-success",
          { loading: true, autoClose: false }
        ),
    });
  };

  const handleToggle = () => {
    setInterval((prev) => ({
      interval: intervalData?.interval ? intervalData?.interval : "0 * * * *",
      active: !prev?.active,
    }));
  };

  const handleInterval = (e: ChangeEvent<HTMLInputElement>) => {
    setInterval((prev) => ({ ...prev, interval: e.target.value }));
  };

  const saveSettings = () => {
    setAutomatic(true);

    if (!intervalData?.active) {
      return TenantAPI.deactivateAutomaticBackup()
        .then(() => {
          setInterval({ active: false, interval: "" });
          setCurrent({ active: false, interval: "" });
        })
        .catch((e) => {
          console.error(e);

          setAutomatic(false);
        })
        .finally(() => {
          setAutomatic(false);
        });
    }

    if (intervalData.active) {
      let interval = intervalData.interval ?? "0 * * * *";

      return TenantAPI.deactivateAutomaticBackup()
        .then(() => {
          TenantAPI.backupCloudInterval(interval)
            .then(() => {
              setInterval({ active: true, interval });
              setCurrent({ active: true, interval });
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              setAutomatic(false);
            });
        })
        .catch((e) => {
          console.error(e);

          setAutomatic(false);
        });
    }
  };

  const toggleSort = () => {
    if (sortParam.sort === "" || sortParam.sort === "FileName") {
      setSortParam((prev) => {
        if (prev.order === "") {
          setActive({ ...active, sort: true });
          return {
            sort: "FileName",
            order: "asc",
          };
        }
        if (prev.order === "asc") {
          setActive({ ...active, sort: true });
          return {
            sort: "FileName",
            order: "desc",
          };
        }
        return {
          sort: "Date",
          order: "asc",
        };
      });
    } else {
      setSortParam((prev) => {
        if (prev.order === "asc") {
          setActive((prevAct) => ({ ...prevAct, sort: true }));
          return {
            sort: "Date",
            order: "desc",
          };
        }
        if (prev.order === "desc") {
          setActive((prevAct) => ({ ...prevAct, sort: false }));
          return {
            sort: "",
            order: "",
          };
        }
        return prev;
      });
    }
  };

  useEffect(() => {
    TenantAPI.getActiveBackups()
      .then((res) => {
        const cronValue = res?.data?.data?.cron;
        if (cronValue) {
          setCurrent({ interval: cronValue, active: true });
          setInterval({ interval: cronValue, active: true });
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }, []);

  return (
    <>
      <AppHelmet title="Cloud Backup - Settings" />
      <Container fluid>
        <div className="d-flex justify-content-between">
          <AppPageHeader
            disabled={loadingBackup}
            fullWidth
            borderless={true}
            title="Cloud Backup"
            description="View & manage Cloud backups"
          />
        </div>
        <div className="mt--4">
          <AppNavbar
            tabs={pageTabs}
            activeTab={tab}
            onSelect={handleTabChange}
          />
        </div>
      </Container>
      {tab === "settings" ? (
        <div>
          <div className="pt-3 pb-3 mb-3 ml-3 mr-3 border-bottom border-lighter d-flex justify-content-between">
            <AppPageHeader
              actionButtonName="Apply"
              fullWidth
              action={saveSettings}
              actionButtonColor="primary"
              size="sm"
              title="Automatic Backup Settings"
              disabled={
                (currentIntervalData?.active === intervalData?.active &&
                  currentIntervalData?.active &&
                  intervalData?.interval === currentIntervalData?.interval) ||
                (currentIntervalData?.active === intervalData?.active &&
                  !currentIntervalData?.active) ||
                loadingAutomatic
              }
              description="Configure automatic backups for your GrowthCloud"
            />
          </div>

          <div className="ml-3">
            <h5 className="mb-1">Automatic Backups</h5>
            <p className="small">
              Automatic Backups will take periodic snapshots of your entire
              GrowthCloud that can be used to restore to a specific point in
              time at a later date.
            </p>
            <Toggle
              className="mr-5"
              onChange={handleToggle}
              checked={intervalData?.active ?? false}
              checkedChildren="On"
              unCheckedChildren="Off"
            />

            {intervalData?.active && (
              <>
                <div className="mt-4">
                  <h5 className="mb-1">Automatic Backup Frequency</h5>
                  <p className="small">
                    Select how frequently you'd like Automatic Backups to take a
                    snapshot of your GrowthCloud.
                  </p>
                  <FormGroup className="d-flex justify-content-between">
                    <Col className="m-0 p-0" xs={4} md={6}>
                      <Input
                        onChange={handleInterval}
                        value={intervalData?.interval ?? ""}
                        type="select"
                        bsSize="sm"
                      >
                        <option value="" disabled>
                          --
                        </option>
                        <option value="0 * * * *">Every Hour</option>
                        <option value="0 */8 * * *">Every 8 Hours</option>
                        <option value="0 0 * * *">Every Day</option>
                      </Input>
                    </Col>
                  </FormGroup>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="pt-3 pb-3 mb-3 ml-3 mr-3 border-bottom border-lighter">
            <AppPageHeader
              size="sm"
              title="Manage Backups"
              description="Manage or restore previous cloud backups"
              actionButtonName="Backup Now"
              action={backupCloudNow}
            />
          </div>
          <div className="ml-3">
            <SortBy
              moduleAPIName="CloudBackups"
              custom={[
                {
                  APIName: "FileName",
                  FieldType: "Text",
                  Name: "File Name",
                  Protected: true,
                  ReadOnly: true,
                  order: sortParam.sort === "FileName" ? sortParam.order : "",
                  sortFunction: () => {
                    setSortParam((prev) => {
                      if (prev.sort === "Date") {
                        setActive({ ...active, sort: true });
                        return {
                          sort: "FileName",
                          order: "asc",
                        };
                      }
                      if (prev.order === "") {
                        setActive({ ...active, sort: true });
                        return {
                          sort: "FileName",
                          order: "asc",
                        };
                      }
                      if (prev.order === "asc") {
                        setActive({ ...active, sort: true });
                        return {
                          sort: "FileName",
                          order: "desc",
                        };
                      }
                      setActive({ ...active, sort: false });
                      return {
                        sort: "FileName",
                        order: "",
                      };
                    });
                  },
                },
                {
                  APIName: "Created",
                  FieldType: "Date",
                  Name: "Created",
                  Protected: true,
                  ReadOnly: true,
                  order: sortParam.sort === "Date" ? sortParam.order : "",
                  sortFunction: () => {
                    setSortParam((prev) => {
                      if (prev.sort === "FileName") {
                        setActive((prevAct) => ({ ...prevAct, sort: true }));
                        return {
                          sort: "Date",
                          order: "asc",
                        };
                      }
                      if (prev.order === "") {
                        setActive((prevAct) => ({ ...prevAct, sort: true }));
                        return {
                          sort: "Date",
                          order: "asc",
                        };
                      }
                      if (prev.order === "asc") {
                        setActive((prevAct) => ({ ...prevAct, sort: true }));
                        return {
                          sort: "Date",
                          order: "desc",
                        };
                      }
                      if (prev.order === "desc") {
                        setActive((prevAct) => ({ ...prevAct, sort: false }));
                        return {
                          sort: "Date",
                          order: "",
                        };
                      }
                      return prev;
                    });
                  },
                },
              ]}
              updateSortBy={toggleSort}
            />
            <Whisper
              ref={filterRef}
              placement="autoVerticalEnd"
              trigger="click"
              preventOverflow
              className="popover-open"
              speaker={
                <Popover className="sidebar-popover">
                  <FilterDropdownMenu
                    onApply={(out: any) => {
                      setFilter(out);
                    }}
                    query={filter}
                    currentModule={module}
                    defaultQuery={null}
                    onClear={() => {
                      setFilter(null);
                    }}
                  ></FilterDropdownMenu>
                </Popover>
              }
            >
              <Button
                size="sm"
                color="secondary"
                outline={!filter}
                className="px-2"
              >
                Filter
              </Button>
            </Whisper>
          </div>
          {backupHistoryLoading ? (
            <div className="m-2" style={{ height: "150px" }}>
              <ListItem loading />
              <ListItem loading />
            </div>
          ) : (
            <div className="px-3 pt-3">
              {!!backupHistory && backupHistory?.length > 0 ? (
                backupHistory?.map((backup) => {
                  let subTitle = `Created on ${dayjs(backup.Date).format(
                    "MMMM Do YYYY, h:mm:ss a"
                  )}`;

                  const description = (
                    <div className="tw:m-0 tw:p-0">
                      <small className="tw:flex tw:items-baseline ">
                        <p className="tw:font-bold tw:text-xs tw:py-0 tw:my-0">
                          Backed-up Modules:
                        </p>
                        <p className="tw:text-xs tw:italic tw:py-0 tw:my-0">
                          {backup?.Modules?.join(", ")}
                        </p>
                      </small>
                    </div>
                  );

                  return (
                    <ListItem
                      key={backup._id}
                      icon="mdi-database-refresh"
                      subTitle={subTitle}
                      title={backup.Title}
                      description={
                        backup.Modules?.length ? description : undefined
                      }
                      options={
                        <BackupOptions
                          backupID={backup._id}
                          backupDate={backup.Date}
                        />
                      }
                    />
                  );
                })
              ) : (
                <AppEmptyState title="Backups" description="No backups Found" />
              )}
              <div className="border-top p-3">
                <Row className="align-items-center">
                  <Col xs="12" sm="">
                    <div className="d-inline-block mb-3 mb-sm-0">
                      {backupHistory && (
                        <small>
                          viewing records&nbsp;
                          {minRecords} - {maxRecords}
                        </small>
                      )}
                    </div>
                  </Col>
                  <Col xs="12" sm="auto">
                    <div className="d-inline-block">
                      <Input
                        onChange={(e) =>
                          onChangeCount(parseInt(e.target.value))
                        }
                        value={recordCount}
                        type="select"
                      >
                        <option value={25}>25 Records Per Page</option>
                        <option value={50}>50 Records Per Page</option>
                        <option value={100}>100 Records Per Page</option>
                        <option value={250}>250 Records Per Page</option>
                      </Input>
                    </div>

                    <div className="d-inline-block ml-3 my-4">
                      <nav aria-label="Page navigation example">
                        <Pagination
                          className="pagination justify-content-end mb-0"
                          listClassName="justify-content-end mb-0"
                          size="sm"
                        >
                          <PaginationItem
                            className={!hasBack ? "disabled" : ""}
                          >
                            <PaginationLink
                              href="#"
                              onClick={goBack}
                              tabIndex={-1}
                            >
                              <i className="mdi mdi-chevron-left" />
                              <span className="sr-only">Previous</span>
                            </PaginationLink>
                          </PaginationItem>
                          <PaginationItem
                            className={!hasNext ? "disabled" : ""}
                          >
                            <PaginationLink href="#" onClick={goNext}>
                              <i className="mdi mdi-chevron-right" />
                              <span className="sr-only">Next</span>
                            </PaginationLink>
                          </PaginationItem>
                        </Pagination>
                      </nav>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
